import z from "zod";

export const ActionIdParamsSchema = z.object({
  id: z.string(),
});

// Dry run body schema (optional emails override + optional provenance target,
// e.g. a Notion page for page-driven actions)
export const DryRunBodySchema = z.object({
  emails: z.array(z.string()).optional(),
  source: z.string().optional(),
  sourceRef: z.string().optional(),
  sourceUrl: z.string().optional(),
});

const ActionGrantsSchema = z.record(z.string(), z.boolean());

export const CronSchema = z.object({
  hour: z.string(),
  weekday: z.string(),
  monthDay: z.string(),
  location: z.string(),
  // Absent on every cron stored before sub-hourly intervals existed, and
  // omitted by the backend whenever the action runs hourly or daily.
  minutes: z.number().optional(),
});
export type Cron = z.infer<typeof CronSchema>;

const ScopeSchema = z.object({
  assetIds: z.array(z.string()).optional(),
  conceptIds: z.array(z.string()).optional(),
  labelIds: z.array(z.string()).optional(),
  viewIds: z.array(z.string()).optional(),
});

const WhoSchema = z.object({
  createdBy: z.array(z.string()),
});

const TriggerKindSchema = z.enum(["trigger.new", "trigger.all", "trigger.session.ended", "trigger.calendar.event"]);

const TriggerSchema = z.object({
  kind: TriggerKindSchema,
  who: WhoSchema.optional(),
  sources: z.array(z.string()).optional(),
  containers: z.array(z.string()).optional(),
  requireSourceRef: z.boolean().optional(),
});

const EmailOutputSchema = z.object({
  subject: z.string().optional(),
  to: z.array(z.string()),
  includeDateInSubject: z.boolean().optional(),
});

// channelName and connected come back on every read and are ignored on a write. Once the channel is
// no longer bound to the action's project, connected is false and channelName is empty. A connected
// channel bound before names were kept has an empty channelName too.
const SlackOutputSchema = z.object({
  integrationId: z.string(),
  channelId: z.string(),
  channelName: z.string().optional(),
  connected: z.boolean().optional(),
});
export type ActionSlackOutput = z.infer<typeof SlackOutputSchema>;

// Everywhere a run's result goes besides its chat. Leaving a destination out turns it off.
const OutputSchema = z.object({
  email: EmailOutputSchema.optional(),
  library: z.object({ labelIds: z.array(z.string()).optional() }).optional(),
  slack: SlackOutputSchema.optional(),
});

// Tool flags shared by enabled/disabled tool configurations
const ToolFlagsSchema = z.object({
  webSearch: z.boolean().optional(),
  webPage: z.boolean().optional(),
  knowledgeBase: z.boolean().optional(),
  imageGeneration: z.boolean().optional(),
  chart: z.boolean().optional(),
  presentation: z.boolean().optional(),
  agents: z.boolean().optional(),
  scout: z.boolean().optional(),
  askUser: z.boolean().optional(),
  videoGeneration: z.boolean().optional(),
});

const PromptModeSchema = z.enum(["auto", "fast", "expert", "research"]);

const AutoApproveSchema = z.object({ operations: z.array(z.string()).optional() });

// Pre-session context refresh; absent means enabled with the default lead.
const RefreshSchema = z.object({
  enabled: z.boolean().optional(),
  leadMinutes: z.number().optional(),
});

export const ActionConfigSchema = z.object({
  cron: CronSchema.optional(),
  models: z.array(z.string()).optional(),
  output: OutputSchema.optional(),
  prompt: z.string(), // base64 encoded
  richText: z.string().nullable().optional(), // rich text JSON for frontend display
  scope: ScopeSchema.optional().default({}),
  trigger: TriggerSchema.optional(),
  preLoadedSkills: z.array(z.string()).optional(),
  mode: PromptModeSchema.optional(),
  disabledTools: ToolFlagsSchema.optional(),
  autoApprove: AutoApproveSchema.optional(),
  refresh: RefreshSchema.optional(),
});
export type ActionConfig = z.infer<typeof ActionConfigSchema>;

export const ActionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  config: ActionConfigSchema,
  grants: ActionGrantsSchema.optional(),
  tenantId: z.string(),
  organizationId: z.string(),
  workspaceId: z.string(),
  projectId: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  modifiedBy: z.string(),
  modifiedAt: z.string(),
  isArchived: z.boolean().optional(),
  enabled: z.boolean(),
  // Set only when the action was auto-paused. Known values:
  // "repeated_failures", "creator_unauthorized".
  pausedReason: z.string().optional(),
});
export type Action = z.infer<typeof ActionSchema>;

export const CreateActionInputSchema = z.object({
  name: z.string(),
  description: z.string(),
  config: ActionConfigSchema,
});
export type CreateActionInput = z.infer<typeof CreateActionInputSchema>;

export const UpdateActionInputSchema = z.object({
  name: z.string(),
  description: z.string(),
  config: ActionConfigSchema,
  enabled: z.boolean().optional(),
});
export type UpdateActionInput = z.infer<typeof UpdateActionInputSchema>;

// Transfer action input schema. An absent userId is a self-claim, which is the
// only form the endpoint accepted before directed transfers existed.
export const TransferActionInputSchema = z.object({
  userId: z.string().optional(),
});

export const ActionsSyncSchema = z.object({
  actions: z.record(z.string(), ActionSchema),
});
export type ActionsSync = z.infer<typeof ActionsSyncSchema>;

// Chat schema for DryRun response
export const ChatSchema = z.object({
  id: z.string(),
  title: z.string(),
  locale: z.string(),
  actionId: z.string().nullable().optional(),
  tenantId: z.string(),
  organizationId: z.string(),
  workspaceId: z.string(),
  projectId: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  modifiedBy: z.string(),
  modifiedAt: z.string(),
});

export const ExecutionMetaSchema = z.object({
  actionId: z.string(),
  chatId: z.string(),
  title: z.string().optional().default(""),
  isDryRun: z.boolean().optional().default(false),
  outcomeError: z.string().optional(),
  outcomeAssetId: z.string().optional(),
  credits: z
    .number()
    .nullish()
    .transform((v) => v ?? 0),
  tenantId: z.string(),
  organizationId: z.string(),
  workspaceId: z.string(),
  projectId: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  modifiedBy: z.string(),
  modifiedAt: z.string(),
});
export type ExecutionMeta = z.infer<typeof ExecutionMetaSchema>;

export const ExecutionMetasSchema = z.object({
  records: z.array(ExecutionMetaSchema),
  first_id: z.string().optional(),
  last_id: z.string().optional(),
  has_more: z.boolean(),
});
export type ExecutionMetas = z.infer<typeof ExecutionMetasSchema>;

export const ExecutionsQueryParamsSchema = z.object({
  actionIds: z.array(z.string()).optional(),
  limit: z.number().optional(),
  after_id: z.string().optional(),
  before_id: z.string().optional(),
});
export type ExecutionsQueryParams = z.infer<typeof ExecutionsQueryParamsSchema>;

export const UnsubscribedEmailsSchema = z.object({
  unsubscribed: z.array(z.string()),
});
